import { memo, type KeyboardEvent } from 'react';
import type { Race } from '../../types';
import { getRegionLabel } from '../../utils/regionMapper';
import { formatDistance, sortDistances } from '../../utils/distance';
import { getDday } from '../../utils/dday';
import { formatKorDate } from '../../utils/dateRange';
import { STATUS_LABELS } from '../../utils/raceLabels';
import { BookmarkButton } from '../../races/BookmarkButton';
import './RaceCard.css';

interface RaceCardProps {
  race: Race;
  isActive: boolean;
  onSelect: (race: Race) => void;
  onHover: (race: Race | null) => void;
}

function RaceCardInner({ race, isActive, onSelect, onHover }: RaceCardProps) {
  const regionLabel = race.region ? getRegionLabel(race.region) : (race.city ?? '');
  const dday = getDday(race);
  const sorted = sortDistances(race.distances);
  const primary = sorted[sorted.length - 1];
  const others = sorted.slice(0, -1);

  // D-7 이내 접수중 → 가장 뜨거운 상태. D-day 숫자가 시선을 끈다.
  const isHot =
    race.status === 'open' && Number.isFinite(dday.days) && dday.days >= 0 && dday.days <= 7;

  const isPast = race.status === 'closed' || race.status === 'completed';

  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(race);
    }
  };

  return (
    <div
      className={[
        'race-card',
        `status-${race.status}`,
        isActive && 'is-active',
        isHot && 'is-hot',
        isPast && 'is-past',
      ]
        .filter(Boolean)
        .join(' ')}
      role="button"
      tabIndex={0}
      aria-pressed={isActive}
      aria-label={`${race.nameLocal} · ${regionLabel} · ${dday.caption ? `${dday.caption} ${dday.label}` : dday.label}`}
      onClick={() => onSelect(race)}
      onKeyDown={handleKey}
      onMouseEnter={() => onHover(race)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(race)}
      onBlur={() => onHover(null)}
    >
      <div className="race-card-top">
        <span className="race-card-dday display">{dday.label}</span>
        {dday.caption && <span className="race-card-dday-caption">{dday.caption}</span>}
        <span className={`race-card-status status-${race.status}`}>
          {STATUS_LABELS[race.status]}
        </span>
        <span className="race-card-bookmark">
          <BookmarkButton
            raceId={race.id}
            raceName={race.nameLocal || race.name}
            size="sm"
          />
        </span>
      </div>

      <div className="race-card-middle">
        {primary && (
          <span className="race-card-distance-primary display">{formatDistance(primary)}</span>
        )}
        {others.length > 0 && (
          <span className="race-card-distance-others">
            {others.map((d) => (
              <span key={d} className="race-card-distance-chip">
                {formatDistance(d)}
              </span>
            ))}
          </span>
        )}
      </div>

      <div className="race-card-bottom">
        <span className="race-card-name">{race.nameLocal}</span>
        <span className="race-card-meta">
          <span className="race-card-region">{regionLabel}</span>
          <span className="race-card-dot" aria-hidden="true">·</span>
          <span className="race-card-date">{formatKorDate(race.date, 'month-day')}</span>
        </span>
      </div>
    </div>
  );
}

export const RaceCard = memo(RaceCardInner);
